import React, { FC } from "react";
import { Box, TextField } from "@mui/material";
import { InputBaseComponentProps } from "@mui/material/InputBase";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import Badge from "../../../../../assets/hamaspik/Badge";
import { formatPifDate, parsePifDate } from "../../pifDates";
import { pifFieldId } from "../../pifFocus";
import { isPifDobFuture, pifNameFormatMessage, pifSsnFormatMessage } from "../../pifValidation";
import { PifFlow, PifFormValues, formatPifSsn, isPifSsnEditable, pifTypedSsn, toDigitsOnly } from "../../types";
import PifField from "./PifField";
import PifSectionCard from "./PifSectionCard";

interface PifSectionApplicantProps {
  flow: PifFlow;
  values: PifFormValues;
  onChange: (patch: Partial<PifFormValues>) => void;
}

const APPLICANT_NOTE: Record<PifFlow, string> = {
  default: "",
  new: "Exactly as it appears on their government ID.",
  active: "These come from the employee record. If something's wrong, the employee changes it in self-service — not here.",
  inactive: "Pulled from their old record. Confirm the spelling out loud; names and details drift over the years.",
};

/** Fully-redacted SSN — no digits at all. */
const SSN_REDACTED = "•••-••-••••";
const SSN_BULLET = "•";
/* The bullet sits on the font baseline, so it reads as the bottom of the digits. Nudge only the
   dots up onto the digit center. The last four stay on the same line as the date field (Bug 82084). */
const SSN_DOT_SHIFT = "-1.5px";

const SsnMaskInput = React.forwardRef<HTMLInputElement, InputBaseComponentProps>(function SsnMaskInput(props, ref) {
  const { className, value, ownerState, ...rest } = props as InputBaseComponentProps & { ownerState?: unknown };
  void ownerState;
  const text = value === undefined || value === null ? "" : String(value);
  return (
    <div className={className} style={{ position: "relative" }}>
      <input {...rest} ref={ref} value={text} readOnly style={{ position: "absolute", opacity: 0, inset: 0, width: "100%", height: "100%", padding: 0, border: 0 }} />
      <span aria-hidden="true" style={{ lineHeight: "1.4375em" }}>
        {Array.from(text).map((ch, index) =>
          ch === SSN_BULLET ? (
            <span key={index} data-ssn-dot="true" style={{ display: "inline-block", transform: `translateY(${SSN_DOT_SHIFT})` }}>
              {ch}
            </span>
          ) : (
            <span key={index}>{ch}</span>
          ),
        )}
      </span>
    </div>
  );
});

/**
 * What the Social Security Number field shows. Never the raw SSN: at most the last four, and only
 * for a new applicant, whose number the supervisor typed themselves moments earlier.
 *
 * Two sources, because the digits live in different places at different times (Bug 82803):
 *  - while the form is being created, `q` holds what was typed on the start screen;
 *  - on a reopened form `q` is gone (it is session-only), so the last four come from the API's
 *    already-masked `ssnMasked` ('***-**-####').
 * Either way the output uses the form's bullet style rather than the API's asterisks, so the
 * field looks the same in both modes.
 *
 * Existing employees and rehires always fall through to the full mask: they maintain their SSN on
 * their Enterprise record, and the Epic forbids showing it here - "not even the last four".
 */
const ssnDisplayValue = (flow: PifFlow, f: PifFormValues): string => {
  if (flow !== "new") return SSN_REDACTED;
  // pifTypedSsn returns "" unless `q` is a full nine digits — an unmatched Employee ID also opens
  // this flow, and its last four are not an SSN. The API's mask contributes exactly four digits.
  const typed = pifTypedSsn(f);
  const lastFour = typed ? typed.slice(-4) : toDigitsOnly(f.ssnMasked);
  return lastFour.length === 4 ? `•••-••-${lastFour}` : SSN_REDACTED;
};

const PifSectionApplicant: FC<PifSectionApplicantProps> = ({ flow, values: f, onChange }) => {
  const lockPersonal = flow === "active";
  const dobFuture = flow === "new" && isPifDobFuture(f.dob);
  // Open only when the form does not already know the SSN: a new applicant whose lookup entry was
  // a hiring number, not a nine-digit SSN (Bug 82668). A nine-digit entry is the SSN, so it stays
  // masked and locked exactly as before, and so does every flow that reads it off an enterprise
  // record (AC3/AC4) and a reopened form whose SSN is already on file (ssnMasked, Bug 82803).
  const editableSsn = isPifSsnEditable(flow, f);
  // Bug 83251: a partial SSN saves with the draft but cannot be submitted, so say so on the field.
  // Review lists the same problem (getPifGaps) and blocks signing on it.
  const ssnError = editableSsn ? pifSsnFormatMessage(f.q) : null;

  return (
    <PifSectionCard
      id="sec-applicant"
      title="Applicant"
      note={APPLICANT_NOTE[flow]}
      badge={lockPersonal ? <Badge size="sm">FROM RECORD</Badge> : undefined}
    >
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 1.75 }}>
        {/* The ids are what a validation error focuses — see pifFocus / getPifGaps. */}
        <PifField label="First name *" error={lockPersonal ? undefined : pifNameFormatMessage(f.firstName) ?? undefined}>
          <TextField id={pifFieldId("firstName")} size="small" fullWidth value={f.firstName} disabled={lockPersonal} error={!lockPersonal && !!pifNameFormatMessage(f.firstName)} onChange={(e) => onChange({ firstName: e.target.value })} />
        </PifField>
        <PifField label="Middle name" error={lockPersonal ? undefined : pifNameFormatMessage(f.middleName) ?? undefined}>
          <TextField id={pifFieldId("middleName")} size="small" fullWidth value={f.middleName} disabled={lockPersonal} error={!lockPersonal && !!pifNameFormatMessage(f.middleName)} onChange={(e) => onChange({ middleName: e.target.value })} />
        </PifField>
        <PifField label="Last name *" error={lockPersonal ? undefined : pifNameFormatMessage(f.lastName) ?? undefined}>
          <TextField id={pifFieldId("lastName")} size="small" fullWidth value={f.lastName} disabled={lockPersonal} error={!lockPersonal && !!pifNameFormatMessage(f.lastName)} onChange={(e) => onChange({ lastName: e.target.value })} />
        </PifField>
        <PifField label="Known as" error={pifNameFormatMessage(f.knownAs) ?? undefined}>
          <TextField id={pifFieldId("knownAs")} size="small" fullWidth value={f.knownAs} placeholder="Preferred name, optional" error={!!pifNameFormatMessage(f.knownAs)} onChange={(e) => onChange({ knownAs: e.target.value })} />
        </PifField>
        <PifField label="Date of birth *" error={dobFuture ? "Date of birth can't be in the future." : undefined}>
          <DatePicker
            value={parsePifDate(f.dob)}
            disabled={flow !== "new"}
            disableFuture
            onChange={(value) => onChange({ dob: formatPifDate(value) })}
            slotProps={{
              textField: {
                id: pifFieldId("dob"),
                size: "small",
                fullWidth: true,
                error: dobFuture,
                inputProps: { readOnly: true },
              },
            }}
          />
        </PifField>
        <PifField label="Social Security Number" error={ssnError ?? undefined}>
          {editableSsn ? (
            <TextField
              id={pifFieldId("q")}
              size="small"
              fullWidth
              value={f.q}
              error={!!ssnError}
              placeholder="XXX-XX-XXXX"
              inputMode="numeric"
              autoComplete="off"
              onChange={(e) => onChange({ q: formatPifSsn(e.target.value) })}
              inputProps={{ maxLength: 11, "aria-label": "Social Security Number" }}
              data-testid="postif-ssn"
            />
          ) : (
            <TextField
              size="small"
              fullWidth
              disabled
              /* Bug 82803: reopening a saved new-applicant form used to show an em-dash here, which
                 reads as "no SSN on file" rather than "on file, hidden" - see ssnDisplayValue. */
              value={ssnDisplayValue(flow, f)}
              /* Epic 79820/AC3 names this hook (`postif-ssn[data-masked=true]`) but it was never
                 added, so nothing could assert on the field. data-masked mirrors PifSectionDynamic's
                 masked-field control. */
              inputProps={{ "data-masked": true, "aria-label": "Social Security Number, hidden" }}
              InputProps={{ inputComponent: SsnMaskInput }}
              data-testid="postif-ssn"
            />
          )}
        </PifField>
      </Box>
    </PifSectionCard>
  );
};

export default PifSectionApplicant;
